import React, { FC, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { URL } from '../constants/url'
import ErrorAlert from '../components/common/ErrorAlert'
import { RootState } from '../../states/containers/store'
import Header from '../components/common/Header'
import SideBar from '../components/common/SideBar'
import { fetchAccessToken } from '../../states/containers/auth/loginSlice'
import { fetchCommonPart, setStateLanguage } from '../../states/containers/common/commonPartSlice'
import { createModalEventLog, fetchModalEventList } from '../../states/containers/modalEvent/listSlice'
import { fetchNoticeList } from '../../states/containers/notice/listSlice'
import { createTosAndPrivacyPolicyAgreement, fetchTosAndPrivacyPolicy } from '../../states/containers/tosAndPrivacyPolicy/tosAndPrivacyPolicySlice'
import { diffNowToDate, format, nowDate, prevMonth, startOfNextWeek, yesterDay } from '../../utils/date'
import { fetchProfile } from '../../states/containers/setting/profileSlice'
import Modal from '../components/common/Modal'
import { ModalEvent } from '../../states/types/modalEvent'
import { useAlert } from 'react-alert'
import { Form, Field } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { LangValue } from '../../states/types/common'
import { hasRead } from '../../utils/introPage'
import { isManager, isMaster, isAdministrator } from '../../utils/role'
import { getGetPrams, setPath } from '../../utils/url'
import Loading from '../components/common/Loading'
import SideBarSub from '../components/common/SideBarSub'
import { fetchReportList } from '../../states/containers/report/reportSlice'
import WorkStyleModal from '../components/common/WorkStyleModal'
import WorkStyleConfirmModal from '../components/common/WorkStyleConfirmModal'
import SideBarSubAnalyze from '../components/common/SideBarSubAnalyze'
import SideBarSubTask from '../components/common/SideBarSubTask'
import SideBarSubRisk from '../components/common/SideBarSubRisk'
import PromotionModal from '../components/common/PromotionModal'
import SideBarSubSetting from '../components/common/SideBarSubSetting'
import { updateCurrentDate, updateSideOpen } from '../../states/containers/common/currentSlice'
import InfoBar from '../components/common/InfoBar'
import { fetchInformation } from '../../states/containers/information/informationSlice'
import { fetchRiskMembers, fetchRiskTeams } from '../../states/containers/risk/riskSlice'
import SideBarSubSearch from '../components/common/SideBarSubSearch'
import ExtraOrderModal from '../components/common/ExtraOrderModal'

interface TosAndPrivacyPolicy {
  tosCheck: boolean
  privacyCheck: boolean
}

const Main: FC = (props) => {
  const dispatch = useDispatch()
  const alert = useAlert()
  const history = useHistory()

  const defaultLang = 'ja'
  const [t, i18n] = useTranslation()
  const { language } = useParams<{ language: LangValue }>()
  const browserLang = 'ja'
  const location = useLocation()

  const {
    commonPart,
    login,
    tosAndPrivacyPolicy,
    modalEventList,
    report,
    profile,
    current,
  } = useSelector((state: RootState) => state)

  useEffect(() => {
    const lang = defaultLang || language || browserLang
    if (lang !== "ja" && lang !== "en") {
      return
    }

    i18n.changeLanguage(lang)
    dispatch(setStateLanguage(lang as "ja" | "en"))
  }, [language])

  const [showTosAndPrivacyPolicyModal, setShowTosAndPrivacyPolicyModal] = useState(false)
  const [tosAndPrivacyPolicyValues, setTosAndPrivacyPolicyValues] = useState<TosAndPrivacyPolicy>({
    tosCheck: false,
    privacyCheck: false,
  })

  const alreadyPaidOrDuringTrial = useMemo(() => (
    commonPart.payment_finished || diffNowToDate(String(commonPart.trial_end_date)) >= 0
  ), [commonPart])

  const [showUserTosAgreeModal, setShowUserTosAgreeModal] = useState(false)
  const [showModalEvent, setShowModalEvent] = useState(false)
  const [modalEventDetail, setModalEventDetail] = useState<ModalEvent | null>(null)
  const [isDisplayWorkStyleModal, setIsDisplayWorkStyleModal] = useState(false)
  const [isDisplayWorkStyleConfirmModal, setIsDisplayWorkStyleConfirmModal] = useState(false)
  const [isDisplayPromotionModal, setIsDisplayPromotionModal] = useState(false)
  const [promotionType, setPromotionType] = useState<string>("")
  const [displayExtraOrder, setDisplayExtraOrder] = useState<boolean>(false)

  const isAllIncludes = (arr: number[], target: number[]) => arr.every((el: number) => target.includes(el))

  const showTutorialModal = () => {
    if (isManager(login.role) || isMaster(login.role)) {
      history.push(setPath(URL.TUTORIAL_MASTER.path, 'ja'))
    } else {
      history.push(setPath(URL.TUTORIAL_MEMBER.path, 'ja'))
    }
    setShowUserTosAgreeModal(false)
  }

  const [hasAccessToken, setHasAccessToken] = useState<boolean>(false)

  useEffect(() => {
    dispatch(fetchAccessToken(
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      () => {
        if (!hasAccessToken) {
          dispatch(fetchProfile())
          dispatch(fetchInformation())
          setHasAccessToken(true)
        }
      },
      () => {
        if (location.pathname !== URL.LOGIN.path) {
          history.push(`/?redirect_path=${location.pathname}`)
        } else {
          history.push(URL.LOGIN.path)
        }
      },
    ))
    if (!hasAccessToken) return

    if ((location.pathname.match(/dashboard/))) {
      history.push(setPath(URL.TOP.path, 'ja'))
    }

    if (!commonPart.updateCommonPartDay || 0 < diffNowToDate(commonPart.updateCommonPartDay)) {
      dispatch(fetchCommonPart())
      dispatch(fetchTosAndPrivacyPolicy())
      dispatch(fetchNoticeList())
      dispatch(fetchModalEventList())
    }
    document.getElementsByClassName('bg')[0]?.classList.remove('excellent')
    if (
      (location.pathname.match(URL.REPORT.path) && isManager(login.role))
      || ((location.pathname.match(URL.ANALYZE.path) || location.pathname.match(URL.EXPORT.path)) && isManager(login.role))
      || (location.pathname.match(URL.RISK.path) && isManager(login.role))
    ) {
      if (!report.reportList) {
        dispatch(fetchReportList({ target_date: Number(nowDate("DD")) <= 8 ? prevMonth(nowDate(), "YYYY-MM-01") : nowDate("YYYY-MM-01") }))
      }
    }
    if (
      location.pathname.match(URL.DATA_SUMMARY.path)
      || location.pathname.match(URL.GANTT_VIEW.path)
      || location.pathname.match(URL.GOOGLE_CALLBACK.path)
      || location.pathname.match(URL.MICROSOFT_CALLBACK.path)
      || location.pathname.match(URL.CYBOZU_CALLBACK.path)
      || location.pathname.match(URL.FREEE_CALLBACK.path)
      || location.pathname.match(URL.CUSTOM_ALERT.path)
    ) {
      dispatch(updateSideOpen(false))
    } else {
      dispatch(updateSideOpen(true))
    }
    dispatch(fetchInformation())
  }, [location.pathname, commonPart, hasAccessToken])

  useEffect(() => {
    if (commonPart.read_first_intro_ids === undefined) return
    if (login.password_reset === undefined) return
    if (location.pathname.includes(URL.TUTORIAL_MASTER.path) || location.pathname.includes(URL.TUTORIAL_MEMBER.path)) return
    if (commonPart.is_first_manager && !hasRead(URL.TUTORIAL_MASTER.path, commonPart.read_first_intro_ids)) {
      showTutorialModal()
      return
    }
    if (!hasRead(URL.TUTORIAL_MASTER.path, commonPart.read_first_intro_ids)
        && !hasRead(URL.TUTORIAL_MEMBER.path, commonPart.read_first_intro_ids)) {
      if (login.password_reset) {
        showTutorialModal()
      } else {
        setShowUserTosAgreeModal(true)
      }
    }
    const onlyAdminPath = `
      ${URL.SETTING_MEMBER.path}
      | ${URL.SETTING_TEAM.path}
      | ${URL.SETTING_COMPANY.path}
      | ${URL.SETTING_ADMIN.path}
      | ${URL.SETTING_IMPORT_CSV.path}
      | ${URL.SETTING_LANGUAGE.path}
      | ${URL.SETTING_MEASUREMENT.path}
      | ${URL.SETTING_WEB_BROWSER.path}
      | ${URL.SETTING_MEASUREMENT_RESTRICTION.path}
      | ${URL.SETTING_IP_RESTRICTION.path}
      | ${URL.SETTING_CUSTOM_ALERT.path}
      | ${URL.SETTING_CONTRACT.path}
    `
    if (location.pathname.match(onlyAdminPath) && !isAdministrator(login.role)) {
      history.push(setPath(URL.SETTING_PROFILE.path, 'ja'))
      return
    }

    if (location.pathname.match(URL.SETTING_CONTRACT.path)
      && (
        !commonPart.payment_finished
        || !login.change_contract
      )
    ) {
      history.push(setPath(URL.SETTING_PROFILE.path, 'ja'))
      return
    }

    if (location.pathname.match(URL.SETTING.path)) {
      return
    }

    if (
      (isAdministrator(login.role) && location.pathname.match(URL.ANALYZE.path) && login.function_enabled_state?.organizational_analysis)
      || (isAdministrator(login.role) && location.pathname.match(URL.EXPORT.path) && login.function_enabled_state?.organizational_analysis)
      || (location.pathname.match(URL.REPORT.path) && login.function_enabled_state?.ai_work_assessment)
      || (location.pathname.match(URL.TASK.path) && login.function_enabled_state?.task_management)
      || (
        (
          login.function_enabled_state?.organizational_analysis
          || login.function_enabled_state?.ai_work_assessment
          || login.function_enabled_state?.task_management
        )
        && location.pathname.match(URL.SEARCH.path)
      )
      || (location.pathname.match(URL.RISK.path) && login.function_enabled_state?.risk_assessment)
      || (location.pathname.match(URL.DATA_SUMMARY.path) && login.function_enabled_state?.self_management)
      || location.pathname.match(URL.GANTT_VIEW.path)
      || location.pathname.match(URL.CUSTOM_ALERT.path)
      || (location.pathname.match(URL.GOOGLE_CALLBACK.path))
      || (location.pathname.match(URL.MICROSOFT_CALLBACK.path))
      || (location.pathname.match(URL.CYBOZU_CALLBACK.path))
      || (location.pathname.match(URL.FREEE_CALLBACK.path))
    ) {
      history.push(location.pathname + location.search)
      return
    }
    if (login.function_enabled_state?.task_management) {
      history.push(setPath(URL.TASK.path, 'ja'))
      return
    }
    if (
      (!login.function_enabled_state?.organizational_analysis
        && !login.function_enabled_state?.ai_work_assessment
        && !login.function_enabled_state?.task_management
      )
    ) {
      history.push(setPath(URL.SETTING_PROFILE.path, 'ja'))
      return
    }
    history.push(setPath(URL.SETTING_PROFILE.path, 'ja'))
  }, [commonPart])

  useEffect(() => {
    if (!modalEventList.modal_event.length) {
      setModalEventDetail(null)
      setShowModalEvent(false)
    } else {
      setModalEventDetail({
        id: modalEventList.modal_event[0].id,
        title: modalEventList.modal_event[0].title,
        text: modalEventList.modal_event[0].text,
        event_type: modalEventList.modal_event[0].event_type,
      })
      setShowModalEvent(true)
    }
  }, [modalEventList.modal_event])

  const closeEventModal = (modal_event_id: number) => {
    dispatch(
      createModalEventLog(
        { modal_event_id: modal_event_id },
      ),
    )
    setShowModalEvent(false)
  }

  useEffect(() => {
    if (tosAndPrivacyPolicy.version === null) return
    setShowTosAndPrivacyPolicyModal(!tosAndPrivacyPolicy.agreed)
  }, [tosAndPrivacyPolicy])

  useEffect(() => {
    if (!profile.name || !commonPart.updateCommonPartDay) return
    if (location.pathname.includes(URL.TUTORIAL_MASTER.path) || location.pathname.includes(URL.TUTORIAL_MEMBER.path)) return
    if (profile.work_style_ids.length && profile.occupation_id) return
    if (profile.isLoading) return
    if (!commonPart.read_first_intro_ids?.length) return
    if (commonPart.read_first_intro_ids?.length
      && (commonPart.read_first_intro_ids.includes(6) || commonPart.read_first_intro_ids.includes(7))
    ) {
      setTimeout(() => {
        setIsDisplayWorkStyleModal(true)
      }, 1000)
    }
  }, [commonPart, profile])

  useEffect(() => {
    if (!profile.name || !commonPart.updateCommonPartDay) return
    if (location.pathname.includes(URL.TUTORIAL_MASTER.path) || location.pathname.includes(URL.TUTORIAL_MEMBER.path)) return
    if (!commonPart.read_first_intro_ids?.length) return
    if (commonPart.read_first_intro_ids?.length
      && (commonPart.read_first_intro_ids.includes(6) || commonPart.read_first_intro_ids.includes(7))
      && (profile.work_style_ids.length && profile.occupation_id)
      && (Number(nowDate('YYYYMMDD')) > Number(format(startOfNextWeek(profile.work_style_updated_at || nowDate()), 'YYYYMMDD')) + 1)
      && !commonPart.read_first_intro_ids.includes(9)
      && profile.viewable_my_latest_weekly_report
    ) {
      setTimeout(() => {
        setIsDisplayWorkStyleConfirmModal(true)
      }, 1000)
    }
  }, [commonPart, profile])

  useEffect(() => {
    const redirect_mail_page = 'setting/personal|setting/system'
    if (location.pathname.match(redirect_mail_page)) {
      history.push(setPath(URL.SETTING_MAIL_DELIVERY.path, 'ja'))
    }

    if (location.pathname.match('/setting$')) {
      history.push(setPath(URL.SETTING_PROFILE.path, 'ja'))
    }
  }, [])

  useEffect(() => {
    const ele = document.getElementById("launcher")
    if (!ele) return
    setTimeout(() => {
      if (location.pathname.match('task_labeling')) {
        ele.style.opacity = "0"
      } else {
        ele.style.opacity = "1"
      }
    }, 1000)
  }, [location.pathname, document.getElementById("launcher")])

  useEffect(() => {
    if (!login.role || !login.accessToken) return
    if (location.pathname.match(URL.TASK.path) && !login.function_enabled_state?.task_management) {
      history.push(setPath(URL.TOP.path, 'ja'))
      return
    }
    if (location.pathname.match(URL.TASK.path)
      && !location.pathname.match(URL.TASK_LABELING_SELF.path)
      && !location.pathname.match(URL.TASK_APPROVAL_SELF.path)
      && !login.permissions?.task_report_view
      && !login.permissions?.task_project_setting
      && !login.permissions?.task_attendance_export
      && !login.permissions?.task_approver
      && !isAdministrator(login.role)
    ) {
      history.push(setPath(URL.TASK_LABELING_SELF.path, 'ja'))
      return
    }
    if (location.pathname === `/ja${URL.TASK.path}`
      && !login.permissions?.task_report_view
    ) {
      history.push(setPath(URL.TASK_LABELING_SELF.path, 'ja'))
      return
    }
    if (location.pathname.match(URL.TASK.path)
      && !login.function_enabled_state?.task_management_approval
      && (
        location.pathname.match(URL.TASK_APPROVAL_SELF.path)
        || location.pathname.match(URL.TASK_APPROVAL_SETTING.path)
        || location.pathname.match(URL.TASK_APPROVAL_STATUS.path)
        || location.pathname.match(URL.TASK_APPROVAL_STATUS_DETAIL.path)
      )
    ) {
      history.push(setPath(URL.TASK_LABELING_SELF.path, 'ja'))
      return
    }
    if (
      (location.pathname.match(URL.REPORT.path) && !login.function_enabled_state?.ai_work_assessment)
      || (location.pathname.match(URL.ANALYZE.path) && !login.function_enabled_state?.organizational_analysis)
      || (location.pathname.match(URL.RISK.path) && !login.function_enabled_state?.risk_assessment)
      || (location.pathname.match(URL.DATA_SUMMARY.path) && !login.function_enabled_state?.self_management)
    ) {
      if (login.function_enabled_state?.task_management) {
        history.push(setPath(URL.TASK.path, 'ja'))
        return
      }
      history.push(setPath(URL.SETTING_PROFILE.path, 'ja'))
      return
    }
    if (location.pathname === `/ja${URL.SEARCH.path}`
      && login.function_enabled_state?.task_management
      && !login.function_enabled_state?.ai_work_assessment
      && (
        !login.function_enabled_state?.organizational_analysis
        || !isManager(login.role)
      )
    ) {
      history.push(setPath(URL.SEARCH.path, 'ja'))
    }
  }, [login, login.accessToken, location.pathname])

  useEffect(() => {
    if (!login.accessToken) return
    const getParams = getGetPrams(location.search) as {date: string}
    if (location.pathname.match(URL.TASK.path) && !getParams.date && current.targetDate === yesterDay("YYYY-MM-DD")) {
      dispatch(updateCurrentDate(nowDate("YYYY-MM-DD")))
    }
    dispatch(fetchRiskMembers())
    dispatch(fetchRiskTeams())
  }, [login.accessToken])

  const setTosCheck = () => {
    setTosAndPrivacyPolicyValues({
      ...tosAndPrivacyPolicyValues,
      tosCheck: !tosAndPrivacyPolicyValues.tosCheck,
    })
  }

  const setPrivacyCheck = () => {
    setTosAndPrivacyPolicyValues({
      ...tosAndPrivacyPolicyValues,
      privacyCheck: !tosAndPrivacyPolicyValues.privacyCheck,
    })
  }

  const handleTosAndPrivacyPolicyRegister = () => {
    if (!tosAndPrivacyPolicyValues.tosCheck || !tosAndPrivacyPolicyValues.privacyCheck) {
      alert.error(t('layouts.alert.no_checked'))
      return
    }
    if (tosAndPrivacyPolicy.version === null) return
    dispatch(createTosAndPrivacyPolicyAgreement({ version: tosAndPrivacyPolicy.version }, () => {
      alert.success(t('layouts.alert.agreement_sucess'))
      setShowTosAndPrivacyPolicyModal(false)
    }))
  }

  const renderTosAndPrivacyPolicyModal = () => (
    <>
      <h3 id="plan-table-modal-title">{t('layouts.render_tos_and_privacy_policy.title')}</h3>
      <div className="modal-body" style={{ margin: "1em" }}>
        <Form
          initialValues={tosAndPrivacyPolicyValues}
          onSubmit={handleTosAndPrivacyPolicyRegister}
          render={({ handleSubmit }) => (
            <div style={{ textAlign: "center" }}>
              <p
                style={{
                  margin: "24px",
                  fontSize: "13px",
                  marginBottom: "36px",
                }}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: t('layouts.render_tos_and_privacy_policy.description') }}
              />
              <form onSubmit={handleSubmit}>
                <a
                  href="https://view.qasee.jp/tos_privacy/tos"
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontWeight: "bold" }}
                >
                  {t('layouts.render_tos_and_privacy_policy.tos')}
                </a>
                <br />
                <Field
                  id="tosCheck"
                  name="tosCheck"
                  component="input"
                  type="checkbox"
                  checked={tosAndPrivacyPolicyValues.tosCheck}
                  style={{ margin: "12px 0 12px" }}
                  onClick={setTosCheck}
                />
                <label
                  htmlFor="tosCheck"
                  style={{
                    fontFamily: "sans-serif",
                    fontSize: "14px",
                    lineHeight: "16px",
                    color: "#686767",
                    margin: "0 6px 0",
                    letterSpacing: "0.08em",
                  }}
                >
                  {t('layouts.render_tos_and_privacy_policy.tos_agreement')}
                </label>
                <br />
                <div style={{ margin: "20px" }} />
                <a
                  href="https://view.qasee.jp/tos_privacy/privacy"
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontWeight: "bold" }}
                >
                  {t('layouts.render_tos_and_privacy_policy.privacy')}
                </a>
                <br />
                <Field
                  id="privacyCheck"
                  name="privacyCheck"
                  component="input"
                  type="checkbox"
                  checked={tosAndPrivacyPolicyValues.privacyCheck}
                  style={{ margin: "12px 0 12px" }}
                  onClick={setPrivacyCheck}
                />
                <label
                  htmlFor="privacyCheck"
                  style={{
                    fontFamily: "sans-serif",
                    fontSize: "14px",
                    lineHeight: "16px",
                    color: "#686767",
                    margin: "0 6px 0",
                    letterSpacing: "0.08em",
                  }}
                >
                  {t('layouts.render_tos_and_privacy_policy.privacy_agreement')}
                </label>
                <div style={{ textAlign: "right", margin: "16px" }}>
                  <button type="submit" className="btn btn-primary">
                    {t('layouts.render_tos_and_privacy_policy.submit')}
                  </button>
                </div>
              </form>
            </div>
          )}
        />
      </div>
    </>
  )

  const renderUserTosAgreeModal = () => (
    <>
      <h3 id="plan-table-modal-title">{t('layouts.render_user_tos.title')}</h3>
      <div className="modal-body text-center" style={{ margin: "1em" }}>
        <div>
          <p
            style={{
              margin: "24px",
              fontSize: "13px",
              marginBottom: "36px",
            }}
          >
            {t('layouts.render_user_tos.description_first')}
            <br />
            {t('layouts.render_user_tos.description_second')}
          </p>
          <a
            href="https://view.qasee.jp/tos_privacy/user_tos"
            target="_blank"
            rel="noreferrer"
            style={{ fontWeight: "bold" }}
          >
            {t('layouts.render_user_tos.link')}
          </a>
        </div>
        <div style={{ textAlign: "right", margin: "16px" }}>
          <button type="button" className="btn btn-primary" onClick={showTutorialModal}>
            {t('layouts.render_user_tos.submit')}
          </button>
        </div>
      </div>
    </>
  )

  useEffect(() => {
    if (login.isLoading || login.hide_support_chat) return
    const newEle = document.createElement("script")
    newEle.setAttribute("id", "ze-snippet")
    newEle.setAttribute("src", "https://static.zdassets.com/ekr/snippet.js?key=e66f00d4-5f4f-47c3-b9ce-83c0eea73fbe")
    document.body.appendChild(newEle)
  }, [login.hide_support_chat])

  return (
    <>
      <Header />
      <section className={`wrapper ${((current.sideOpen) && "open")} ${(!alreadyPaidOrDuringTrial && !isMaster(login.role)) && "gone"}`}>
        {
          (alreadyPaidOrDuringTrial || isMaster(login.role)) && (
            <>
              <SideBar
                openPromotionCallback={() => setIsDisplayPromotionModal(true)}
                setTypeCallback={(value) => setPromotionType(value)}
              />
              <SideBarSub
                isOpen={current.sideOpen}
                toggleCallback={() => dispatch(updateSideOpen(!current.sideOpen))}
              />
              <SideBarSubAnalyze
                isOpen={current.sideOpen}
                toggleCallback={() => dispatch(updateSideOpen(!current.sideOpen))}
              />
              <SideBarSubTask
                isOpen={current.sideOpen}
                toggleCallback={() => dispatch(updateSideOpen(!current.sideOpen))}
              />
              <SideBarSubSetting
                isOpen={current.sideOpen}
                toggleCallback={() => dispatch(updateSideOpen(!current.sideOpen))}
              />
              <SideBarSubRisk
                isOpen={current.sideOpen}
                toggleCallback={() => dispatch(updateSideOpen(!current.sideOpen))}
              />
              <SideBarSubSearch
                isOpen={current.sideOpen}
                toggleCallback={() => dispatch(updateSideOpen(!current.sideOpen))}
              />
            </>
          )
        }
        <section className="main">
          {
            commonPart.isLoading ? (
              <Loading />
            ) : (
              <>
                {
                  alreadyPaidOrDuringTrial || isMaster(login.role) ? (
                    <>
                      <InfoBar />
                      <div className="bg" />
                      <div {...props} />
                    </>
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "calc(100vh - 200px)",
                        display: "flex",
                        flexFlow: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        style={{
                          textAlign: "center",
                          fontSize: "40px",
                          fontWeight: "bold",
                          color: "#4472ba",
                          marginBottom: "40px",
                        }}
                      >
                        トライアル期間が終了しました
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          color: "#666666",
                          display: "flex",
                          flexFlow: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "16px",
                        }}
                      >
                        <div>
                          Qaseeをご利用いただき、誠にありがとうございます。
                        </div>
                        <div>
                          トライアル期間が終了しました。
                        </div>
                        <div>
                          PCデータの受信やアラートの通知などができなくなっています。
                        </div>
                        <div>
                          トライアル終了後は、マスター管理者のみページ閲覧することができます。
                        </div>
                        <img src="/static/images/image_1.png" alt="" style={{ width: "200px" }} />
                      </div>
                    </div>
                  )
                }
              </>
            )
          }
        </section>
      </section>
      <ErrorAlert />
      {
        showTosAndPrivacyPolicyModal && (
          <Modal
            show={showTosAndPrivacyPolicyModal}
            closeable={false}
          >
            {renderTosAndPrivacyPolicyModal()}
          </Modal>
        )
      }
      {
        showUserTosAgreeModal && (
          <Modal
            show={showUserTosAgreeModal}
            closeable={false}
          >
            {renderUserTosAgreeModal()}
          </Modal>
        )
      }
      {
        showModalEvent && modalEventDetail && commonPart.read_first_intro_ids?.length
        && (isAllIncludes([1, 7], commonPart.read_first_intro_ids) || isAllIncludes([1, 6], commonPart.read_first_intro_ids)) && (
          <Modal
            show={showModalEvent}
            closeable={false}
            wrapperClass="modal_event"
            closeCallback={() => closeEventModal(modalEventDetail.id)}
            title={modalEventDetail.title}
          >
            <>
              {/* eslint-disable-next-line react/no-danger */}
              <div dangerouslySetInnerHTML={{ __html: modalEventDetail.text }} />
            </>
            <div>
              <button type="button" className="btn btn-primary" style={{ width: "100%" }} onClick={() => closeEventModal(modalEventDetail.id)}>
                {t('model.button.close')}
              </button>
            </div>
          </Modal>
        )
      }
      {/* 働き方モーダル */}
      <WorkStyleModal
        display={isDisplayWorkStyleModal}
        type="force"
        closeCallback={() => setIsDisplayWorkStyleModal(false)}
      />
      {/* 働き方反映されたよモーダル */}
      <WorkStyleConfirmModal
        display={isDisplayWorkStyleConfirmModal}
        closeCallback={() => setIsDisplayWorkStyleConfirmModal(false)}
      />
      <PromotionModal
        display={isDisplayPromotionModal}
        type={promotionType}
        closeCallback={() => setIsDisplayPromotionModal(false)}
        extraOrderCallback={() => {
          setIsDisplayPromotionModal(false)
          setDisplayExtraOrder(true)
        }}
      />
      <ExtraOrderModal
        display={displayExtraOrder}
        closeCallback={() => setDisplayExtraOrder(false)}
      />
    </>
  )
}

export default Main
